import React, { useState } from 'react';
import { MessageCircle, Phone, Mail, FileText, ChevronRight } from 'lucide-react';
import ChatBot from '../components/ChatBot';
import { BRAND } from '../data/brand';
import { useTranslation } from '../i18n/LocaleContext';

const SUPPORT_PHONE = '1900xxxx';
const SUPPORT_EMAIL = `support@${BRAND.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`;

const HelpPage = () => {
  const { t } = useTranslation();
  const [showChatBot, setShowChatBot] = useState(false);

  const helpItems = [
    {
      icon: MessageCircle,
      title: t('chatWithSupport'),
      description: t('directSupport'),
      action: () => setShowChatBot(true)
    },
    {
      icon: Phone,
      title: t('hotline'),
      description: t('hotlineHours'),
      action: () => { window.location.href = `tel:${SUPPORT_PHONE}`; }
    },
    {
      icon: Mail,
      title: t('emailUs'),
      description: SUPPORT_EMAIL,
      action: () => { window.location.href = `mailto:${SUPPORT_EMAIL}`; }
    },
    {
      icon: FileText,
      title: t('userGuide'),
      description: t('viewDocumentation'),
      action: () => { window.alert(t('viewDocumentation')); }
    }
  ];

  const faqs = [
    { q: t('faqDepositQ'), a: t('faqDepositA') },
    { q: t('faqWithdrawQ'), a: t('faqWithdrawA') },
    { q: t('faqCommissionQ'), a: t('faqCommissionA') }
  ];

  return (
    <div className="pb-20 bg-canvas min-h-screen">
      <div className="sticky top-0 z-20 bg-white">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3">
          <h2 className="text-[19px] font-bold text-ink tracking-tight">{t('helpTitle')}</h2>
          <p className="mt-1 text-[12.5px] text-ink-muted">{t('helpSubtitle')}</p>
        </div>
      </div>

      <div className="max-w-md mx-auto">
        <div className="bg-white border-y border-black/[0.05] divide-y divide-black/[0.05]">
          {helpItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.title}
                onClick={item.action}
                className="w-full bg-white px-4 py-3 text-left transition-colors active:bg-gray-50"
              >
                <div className="flex items-center gap-3.5">
                  <span className="w-10 h-10 rounded-lg bg-canvas text-ink flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="flex-1 min-w-0 text-left">
                    <span className="block text-[14.5px] font-semibold text-ink">{item.title}</span>
                    <span className="block text-[12.5px] text-ink-secondary mt-0.5 truncate">{item.description}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-ink-muted flex-shrink-0" />
                </div>
              </button>
            );
          })}
        </div>

        <h3 className="mt-6 mb-2 px-4 text-[14px] font-bold text-ink">{t('faq')}</h3>
        <div className="bg-white border-y border-black/[0.05] divide-y divide-black/5">
          {faqs.map((faq) => (
            <details key={faq.q} className="group px-4 py-3.5">
              <summary className="flex items-center justify-between gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-[13.5px] font-medium text-ink">
                {faq.q}
                <ChevronRight className="w-4 h-4 text-ink-muted flex-shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-2 text-[12.5px] leading-relaxed text-ink-secondary">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>

      <ChatBot isOpen={showChatBot} onClose={() => setShowChatBot(false)} />
    </div>
  );
};

export default HelpPage;
